import { useEffect, useRef, useState } from "react";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { nonAuthInstance } from "../services/axiosConfig";
import { isArray, isEmpty, isString } from "lodash";
import useLoading from "../hooks/useLoading";
import {
  ArrowPathIcon,
  BarsArrowDownIcon,
  ChevronDownIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  Square2StackIcon,
} from "@heroicons/react/24/outline";
import useToast from "../hooks/useToast";
import axios from "axios";
import { format } from "date-fns";
import { utils, write } from "xlsx";
import ImagePreview from "./admin/ImagePreview";
import RenderInWindows from "./admin/RenderInWindow";
import CompareImagesDialog from "../components/dialogs/CompareImagesDialog";
import { fetchImage, fetchUrinalysisImages } from "../services/ api_services";
import { HiOutlineChartSquareBar } from "react-icons/hi";
import { getCurrentDateTimeString } from "../core/Util";
import SelectMenu from "../components/SelectMenu";
import MultipleSelectDropDown from "../components/MultiSelectDropDown";
import ImagePreviewUrinalysis from "./popup/ImagePreviewUrinalysis";
import JSZip from "jszip";
import { ToastTypes } from "../context/ToastProvider";
import { FaFileExcel } from "react-icons/fa6";
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * 필터 필드 레이아웃: 라벨(좌, 고정폭) + 컨트롤(우, 남은 폭).
 * 라벨 폭을 고정해 세로로 정렬이 맞아떨어지게 한다.
 *
 * ⚠️ 반드시 컴포넌트 바깥(모듈 스코프)에 둔다. 컴포넌트 안에서 정의하면
 * 렌더마다 새로운 컴포넌트 타입이 만들어져 React 가 하위 트리를 재마운트하고,
 * 그 안의 input 이 한 글자마다 포커스를 잃어 입력이 안 되는 것처럼 보인다.
 */
const FilterField = ({ label, children }) => (
  <div className="flex items-center gap-x-3">
    <span className="flex w-24 shrink-0 items-center justify-end text-right text-sm font-semibold leading-6 text-gray-900">
      {label}
    </span>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

/**
 * 활성산소 검증 FP 컬럼.
 * 2026-08-03 백엔드 변경으로 단일 `calculated_fp` 가 사라지고, FP 를 6가지 계산 방식
 * (G/M × 원본/보정/235변환 조합) + 가중평균으로 각각 계산한 값이 내려온다.
 * 어느 것이 "정답"인지는 백엔드가 지정하지 않으므로 전부 나란히 보여주고 비교한다.
 */
const OXYGEN_FP_COLUMNS = [
  { id: "calculated_G_fp", label: "G" },
  { id: "calculated_235_M_fp", label: "235_M" },
  { id: "calculated_G_6_fp", label: "G_6" },
  { id: "calculated_M_6_fp", label: "M_6" },
  { id: "calculated_235_G_6_fp", label: "235_G_6" },
  { id: "calculated_235_M_6_fp", label: "235_M_6" },
  { id: "calculated_weight_fp", label: "가중평균" },
];

/**
 * 활성산소 "검증 단계" 표시 여부.
 * 같은 백엔드 변경에서 `calculated_lvl` 이 응답에서 빠졌다(원래도 실계산이 아닌 고정값이었다).
 * 단계에 해당하는 필드가 없어서 화면에서 감춰둔 상태 — 백엔드에 필드 추가를 요청해뒀다.
 * 필드가 생기면 이 값을 true 로 되돌리고 아래 두 곳의 필드명만 맞추면 된다.
 *   1) calculatePieDatas 의 집계 필드
 *   2) 테이블 본문의 검증 단계 셀
 */
const SHOW_VERIFIED_LEVEL = false;
const VERIFIED_LEVEL_FIELD = "calculated_lvl";

export default function Simulation() {
  const sortingFields = [
    { id: "instDt", name: "검사일자" },
    { id: "spec_nm", name: "항목"},
    { id: "brightness", name: "밝기"},
    { id: "fpValue", name: "FP" },
    { id: "difGValue", name: "보정 간격" },
    { id: "lValue", name: "L-상수" },
    { id: "gValue", name: "G-상수" },
  ];

  const defaultDeviceModels = [
    { id: "", name: "전체" },
    { id: "android", name: "안드로이드" },
    { id: "iPhone", name: "아이폰" },
  ];

  // const stripTypes = [
  //     { id: "oxygen", name: "활성산소" },
  //     { id: "urinalysis", name: "소변검사" },
  // ];

  // const stripItems = [
  //     { id: "urobillinogen", name: "우로빌리노겐" },
  //     { id: "glucose", name: "포도당" },
  //     { id: "billirubin", name: "빌리루빈" },
  // ];

  const initialRequestParameters = {
    offset: 0,
    multipler: 0,
    startDate: format(new Date(), "yyyy-MM-dd"),
    endDate: "",
    useBalancedRGB: "Y",
    useRGB1: "Y",
    sortOrders: {instDt: 'DESC'},
    userNm: "",
    userHpNo: "",
    deviceType: "",
    stripType: "",
    stripItems: "",
    stripId: 0,
    custSeq: 0,
    groupCode: "",
    description: "",
    aiChecked: 0,
  };

  /**
   * 백엔드 `simulation.query.max-results` 와 같은 값이어야 한다.
   * 서버가 이 건수에서 결과를 자르므로(페이징 UI 가 없어 전량을 내려받는 구조라 둔 OOM 방어선),
   * 도달하면 "일부만 보고 있다"는 걸 알려야 한다 — 조용히 잘리면 전부 조회한 걸로 오해한다.
   */
  const SIMULATION_MAX_RESULTS = 5000;

  const [list, setList] = useState([]);
  const [listUrinalysis, setListUrinalysis] = useState([]);
  const [resultTruncated, setResultTruncated] = useState(false);
  const [orgList, setOrgList] = useState([]);
  const [gridData, setGridData] = useState([]);

  const { setLoading } = useLoading();
  const [isSearching, setIsSearching] = useState(false); // 조회 버튼 전용 로딩
  const { showToast } = useToast();

  const [requestParameters, setRequestParameters] = useState(initialRequestParameters);
  const [sortOrders, setSortOrders] = useState(initialRequestParameters.sortOrders);

  const [checkBoxCVal, setCheckBoxCVal] = useState(false);
  const [imgPath, setImgPath] = useState("");
  const [imgURL, setImgURL] = useState(null);
  const [detailedTestData, setDetailedTestData] = useState();
  const [openWindow, setOpenWindow] = useState();
  const [openUriWindow, setOpenUriWindow] = useState();
  const [expanded, setExpanded] = useState(false);
  const [summaryCollapsed, setSummaryCollapsed] = useState(true); // 정확도 요약 접기 (기본: 접힘)
  const [selectedSeqs, setSelectedSeqs] = useState([]); // 비교 대상으로 선택한 행
  const [showCompare, setShowCompare] = useState(false);
  const [maskingEnabled, setMaskingEnabled] = useState(true); // 이름·연락처 마스킹 (기본: 적용)
  const [baseLvlPieDatas, setBaseLvlPieDatas] = useState([]);
  const [pieDatas, setPieDatas] = useState([]);

  const [stripTypes, setStripTypes] = useState([]);
  const [stripItems, setStripItems] = useState([]);
  const [images, setImages] = useState([]);
  const [zipFile, setZipFile] = useState();

  const cancelTokenSource = useRef(null);
  const multiStripItemsRef = useRef();

  const COLORS = ["#faeded", "#fae1eb", "#fad4eb", "#fac3ed"];

  const RADIAN = Math.PI / 180;

  useEffect(() => {
    const handleEscapeKey = (event) => {
      // console.log("🚀 ~ handleEscapeKey ~ event:", event.keyCode);
      if (event.keyCode === 27 && cancelTokenSource.current) {
        //console.log("Cancelling request");
        cancelTokenSource.current.cancel(
          "사용자에 의해 요청이 취소되었습니다."
        );
        // source.cancel("Request canceled by the use");
      }
    };

    window.addEventListener("keydown", handleEscapeKey);

    // fetchSimulationDatas();

    return () => {
      window.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  useEffect(() => {
    fetchStripInfos();
    // fetchSimulationDatas();
  }, []);

  useEffect(() => {
    if (!openUriWindow) {
      setImages([]);
      setZipFile(null);
      setDetailedTestData();
    }
  }, [openUriWindow]);

  async function fetchStripInfos() {
    setLoading(true);
    cancelTokenSource.current = axios.CancelToken.source();

    try {
      const response = await nonAuthInstance.get("strip/infos", {
        cancelToken: cancelTokenSource.current.token,
      });

      if (response.status === 200) {
        console.log("response.data: ", response.data);
        setLoading(false);
        if (
          response.data &&
          !isEmpty(response.data) &&
          isArray(response.data)
        ) {
          const convertedData = response.data.map((item) => {
            return {
              id: item.cust_seq + "_" + item.strip_id,
              cust_seq: item.cust_seq,
              name: item.strip_nm + " (" + item.strip_model_nm + ")",
            };
          });
          console.log("🚀 ~ convertedData ~ convertedData:", convertedData);
          setStripTypes(convertedData);
          setRequestParameters((preData) => {
            return {
              ...preData,
              stripType: convertedData[0].id,
            };
          });
        }
      } else {
        setLoading(false);
        setStripTypes([]);
      }
    } catch (e) {
      setLoading(false);
      setStripTypes([]);
      if (axios.isCancel(e)) {
        //console.log("요청이 취소되었습니다:", e.message);
      } else {
        console.error("Error fetching data: ", e);
      }
    }
  }

  async function fetchStripItems(custSeq, stripId) {
    console.log("🚀 ~ fetchStripItems ~ stripId:", stripId);
    console.log("🚀 ~ fetchStripItems ~ custSeq:", custSeq);

    setLoading(true);
    cancelTokenSource.current = axios.CancelToken.source();

    const params = {
      cust_seq: custSeq,
      strip_id: stripId,
    };

    try {
      const response = await nonAuthInstance.get("strip/items", {
        params: params,
        cancelToken: cancelTokenSource.current.token,
      });

      if (response.status === 200) {
        console.log("response.data: ", response.data);
        setLoading(false);
        if (
          response.data &&
          !isEmpty(response.data) &&
          isArray(response.data)
        ) {
          const convertedData = response.data.map((item) => {
            return {
              id: item.spec_code,
              name: item.spec_nm,
              groupCode: item.group_code,
            };
          });
          console.log("🚀 ~ fetchStripItems ~ convertedData:", convertedData);
          setStripItems(convertedData);
        }
      } else {
        setLoading(false);
        setStripItems([]);
      }
    } catch (e) {
      setLoading(false);
      setStripItems([]);
      if (axios.isCancel(e)) {
        //console.log("요청이 취소되었습니다:", e.message);
      } else {
        console.error("Error fetching data: ", e);
      }
    }
  }

  function formatDate(dateString) {
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    };
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", options)
      .format(date)
      .replace(/(\d{2})\/(\d{2})\/(\d{4}),/, "$3-$1-$2");
  }

  /**
   * 활성산소 시뮬레이션 조회.
   * 백엔드가 test/simulation 을 GET → POST 로 바꾸면서 요청 형식도
   * 소변검사(test/retrieve/simulation)와 동일한 DTO(sortOrders/groupCode/specCodes/aiChecked)로 통일됐다.
   * 예전 GET + orderByField/orderBy 로 호출하면 405(Method Not Allowed)가 떨어진다.
   */
  async function fetchSimulationDatas(params) {
    setIsSearching(true);
    setSelectedSeqs([]);

    console.log("🚀 ~ fetchSimulationDatas ~ params:", params);
    // if (cancelTokenSource.current) {
    //     cancelTokenSource.current.cancel("Cancelling pre request");
    // }
    setLoading(true);
    cancelTokenSource.current = axios.CancelToken.source();
    try {
      const response = await nonAuthInstance.post("test/simulation", params, {
        cancelToken: cancelTokenSource.current.token,
      });
      console.log("🚀 ~ fetchSimulationDatas ~ response:", response);
      if (response.status === 200) {
        console.log("response.data: ", response.data);
        if (
          response.data &&
          !isEmpty(response.data) &&
          isArray(response.data)
        ) {
          setResultTruncated(response.data.length >= SIMULATION_MAX_RESULTS);
          calculatePieDatas(response.data);
          response.data.map((item) => {
            item.inst_dt = formatDate(item.inst_dt);
            return item;
          });
          // setPieDatas(createdPieData);
          checkListData(response.data);
          setLoading(false);
          showToast("success", "성공", "조회를 완료했습니다.");
        } else {
          setResultTruncated(false);
          setList([]);
          setGridData([]);
          setLoading(false);
        }
      } else {
        setResultTruncated(false);
        setList([]);
        setGridData([]);
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
      if (axios.isCancel(e)) {
        //console.log("요청이 취소되었습니다:", e.message);
      } else {
        console.error("Error fetching data: ", e);
        showToast(
          ToastTypes.ERROR,
          "오류",
          "시뮬레이션 데이터를 가져올 때 오류가 생겼습니다."
        );
      }
    } finally {
      setIsSearching(false);
    }
  }

  async function callRetrieveSimulation(queryString) {
    setIsSearching(true);
    setSelectedSeqs([]);
    // console.log("🚀 ~ callRetrieveSimulation ~ queryString:", queryString);
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel("Cancelling pre request");
    }
    setLoading(true);
    cancelTokenSource.current = axios.CancelToken.source();
    try {
      const response = await nonAuthInstance.post(
        `test/retrieve/simulation`, queryString,
        {
          cancelToken: cancelTokenSource.current.token,
        },
      );
      if (response.status === 200) {
        console.log("response.data: ", response.data);
        setLoading(false);
        showToast("success", "성공", "조회를 완료했습니다.");
        if (
          response.data &&
          isArray(response.data) &&
          !isEmpty(response.data)
        ) {
          setResultTruncated(response.data.length >= SIMULATION_MAX_RESULTS);
          response.data.map((item) => {
            item.inst_dt = formatDate(item.inst_dt);
            return item;
          });
          setListUrinalysis(response.data);
        } else {
          setResultTruncated(false);
          setListUrinalysis([]);
        }
      } else {
        setLoading(false);
        showToast(
          ToastTypes.ERROR,
          "오류",
          "테스트 데이터 가져올 때 서버에 오류 났습니다. 다시 한번 시도하십시오!"
        );
        setListUrinalysis([]);
      }
    } catch (e) {
      setLoading(false);
      showToast(
        ToastTypes.ERROR,
        "오류",
        "테스트 데이터 가져올 때 서버에 오류 났습니다. 다시 한번 시도하십시오!"
      );
      setListUrinalysis([]);
      if (axios.isCancel(e)) {
        //console.log("요청이 취소되었습니다:", e.message);
      } else {
        console.error("Error fetching data: ", e);
      }
    } finally {
      setIsSearching(false);
    }
  }

  const calculatePieDatas = (data) => {
    let levelCounts = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
    };
    let calculatedlevelCounts = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
    };

    data.forEach((item) => {
      const level = item.insp_level;
      levelCounts[level] = (levelCounts[level] || 0) + 1;
      // 검증 단계 필드가 응답에서 빠진 상태라 집계하지 않는다. (SHOW_VERIFIED_LEVEL 참고)
      if (SHOW_VERIFIED_LEVEL) {
        const calcualtedLvl = item[VERIFIED_LEVEL_FIELD];
        calculatedlevelCounts[calcualtedLvl] =
          (calculatedlevelCounts[calcualtedLvl] || 0) + 1;
      }
      return item;
    });

    //console.log("🚀 ~ createdPieData ~ levelCounts:", levelCounts);
    //console.log("🚀 ~ fetchSimulationDatas ~ calculatedlevelCounts:", calculatedlevelCounts);
    const pieList = [
      { name: "정상", value: calculatedlevelCounts[1] },
      { name: "주의", value: calculatedlevelCounts[2] },
      { name: "높음", value: calculatedlevelCounts[3] },
      { name: "매우높음", value: calculatedlevelCounts[4] },
    ];
    const basePielist = [
      { name: "정상", value: levelCounts[1] },
      { name: "주의", value: levelCounts[2] },
      { name: "높음", value: levelCounts[3] },
      { name: "매우높음", value: levelCounts[4] },
    ];
    //console.log("🚀 ~ fetchSimulationDatas ~ pieList:", pieList);
    //console.log("🚀 ~ fetchSimulationDatas ~ basePielist:", basePielist);
    setBaseLvlPieDatas(basePielist);
    setPieDatas(pieList);
  };

  const toggleSelectRow = (testSeqNum, checked) => {
    setSelectedSeqs((prev) =>
      checked ? [...prev, testSeqNum] : prev.filter((seq) => seq !== testSeqNum)
    );
  };

  /** 선택된 행 원본. 화면에 보이는 목록에서 찾으므로 재조회하면 자연스럽게 정리된다. */
  const getSelectedRows = () => {
    const source = !isEmpty(listUrinalysis) ? listUrinalysis : list;
    return selectedSeqs
      .map((seq) => source.find((item) => item.test_seq_num === seq))
      .filter(Boolean);
  };

  const rgbToCssColor = (inspRgb) => {
    // console.log("🚀 ~ rgbToCssColor ~ inspRgb:", inspRgb);
    if (inspRgb && isString(inspRgb)) {
      const splittedRGB = inspRgb.split(",");
      // console.log("🚀 ~ rgbToCssColor ~ splittedRGB:", splittedRGB);
      return `rgb(${splittedRGB[0]}, ${splittedRGB[1]}, ${splittedRGB[2]})`;
    }
  };

  const handleOnClickSearchButton = () => {
    if (
      !isEmpty(requestParameters.custSeq) &&
      !isEmpty(requestParameters.stripId)
    ) {
      console.log(
        "🚀 ~ useEffect ~ requestParameters.custSeq:",
        requestParameters.custSeq
      );
      console.log(
        "🚀 ~ useEffect ~ requestParameters.stripId:",
        requestParameters.stripId
      );

      // 활성산소/소변검사가 같은 요청 DTO를 쓰므로 한 곳에서 만든다.
      const params = {
        offset: requestParameters.offset,
        levelMultipler: requestParameters.multipler,
        sortOrders: sortOrders,
        useBalancedRGB: requestParameters.useBalancedRGB,
        useRGB1: requestParameters.useRGB1,
        startDate: requestParameters.startDate.toString(),
        endDate: requestParameters.endDate.toString(),
        userNm: (requestParameters.userNm && requestParameters.userNm.trim()) ?? "",
        // 연락처는 하이픈 없이 저장되므로 숫자만 남겨 보낸다(010-1234 입력도 매칭되게).
        userHpNo: (requestParameters.userHpNo && requestParameters.userHpNo.replace(/[^0-9]/g, "")) ?? "",
        deviceModel: requestParameters.deviceType,
        custSeq: requestParameters.custSeq,
        stripId: requestParameters.stripId,
        groupCode:
          requestParameters.groupCode && !isEmpty(requestParameters.groupCode)
            ? requestParameters.groupCode
            : stripItems[0]?.groupCode,
        description: requestParameters.description,
        aiChecked: requestParameters.aiChecked === true,
      };
      params.specCodes = isEmpty(requestParameters.stripItems) && !isEmpty(stripItems)
          ? [stripItems[0].id] : requestParameters.stripItems;
      console.log(params);

      if (isModimUrinalysisStrip() || isShinyanUrinalysisStrip()) {
        // const params = {
        //   offset: requestParameters.offset,
        //   levelMultipler: requestParameters.multipler,
        //   sortOrders: sortOrders,
        //   useBalancedRGB: requestParameters.useBalancedRGB,
        //   useRGB1: requestParameters.useRGB1,
        //   startDate: requestParameters.startDate.toString(),
        //   endDate: requestParameters.endDate.toString(),
        //   userNm:
        //     (requestParameters.userNm && requestParameters.userNm.trim()) ?? "",
        //   deviceModel: requestParameters.deviceType,
        //   custSeq: requestParameters.custSeq,
        //   stripId: requestParameters.stripId,
        //   groupCode:
        //     requestParameters.groupCode && !isEmpty(requestParameters.groupCode)
        //       ? requestParameters.groupCode
        //       : stripItems[0].groupCode,
        //   specCodes: requestParameters.stripItems,
        //   description: requestParameters.description,
        //   aiChecked: requestParameters.aiChecked === true
        // };
        // console.log("🚀 ~ handleOnClickSearchButton ~ params:", params);

        // const searchParams = new URLSearchParams();
        // Object.keys(params).forEach((key) => {
        //   if (key !== "specCodes") {
        //     searchParams.append(key, params[key]);
        //   }
        // });
        // console.log(
        //   "🚀 ~ handleOnClickSearchButton ~ params.specCodes:",
        //   params.specCodes
        // );
        // if (isEmpty(params.specCodes)) {
        //   if (stripItems && !isEmpty(stripItems)) {
        //     searchParams.append("specCodes", stripItems[0].id);
        //   }
        // } else {
        //   if (stripItems && !isEmpty(stripItems)) {
        //     params.specCodes.forEach((code) =>
        //       searchParams.append("specCodes", code)
        //     );
        //   }
        // }

        // console.log("[Search][params] ==> ");
        // for (const [key, value] of searchParams.entries()) {
        //   console.table(`${key}: ${value}`);
        // }

        // const plainParams = {};
        // searchParams.forEach((value, key) => {
        //   if (plainParams[key]) {
        //     // 배열 처리
        //     if (!Array.isArray(plainParams[key])) {
        //       plainParams[key] = [plainParams[key]];
        //     }
        //     plainParams[key].push(value);
        //   } else {
        //     plainParams[key] = value;
        //   }
        // });
        callRetrieveSimulation(params);
      } else {
        fetchSimulationDatas(params);
      }
    }
  };

  const handleOnClickGridRow = (e, testSeqNum) => {
    setSelectedSeqs((prev) =>
      prev.includes(testSeqNum)
        ? prev.filter((seq) => seq !== testSeqNum)
        : [...prev, testSeqNum]
    );
  };

  const handleOnDoubleClickGridRow = async (e, data) => {
    console.log("🚀 ~ handleOnDoubleClickGridRow ~ data:", data);
    //console.log("🚀 ~ handleOnDoubleClickGridRow ~ data.file_path:", data.file_path);
    const replacedImgPath = data.file_path.replace(/^.*\\healthimage\\/, "");
    if (isOxygenStrip()) {
      if (!isEmpty(data.file_path) && isString(data.file_path)) {
        // const replacedImgPath = data.file_path.replace(/^.*\\healthimage\\/, "");
        console.log("🚀 ~ fetchTestImage ~ replacedImgPath:", replacedImgPath);
        const response = await fetchImage(replacedImgPath);
        if (response && response.status === 200) {
          //console.log("🚀 ~ fetchTestImage ~ response:", response);
          const imageUrl = URL.createObjectURL(response.data);
          setImgURL(imageUrl);
          setOpenWindow(!openWindow);
          setDetailedTestData(data);
        } else {
        }
        setImgPath(replacedImgPath);
      }
    } else if (isModimUrinalysisStrip() || isShinyanUrinalysisStrip()) {
      if (!isEmpty(data.file_path) && isString(data.file_path)) {
        // console.log("🚀 ~ fetchTestImage ~ replacedImgPath:", replacedImgPath);
        console.log("Replaced img path ==> ", replacedImgPath);
        console.log("Detailed test data ==> ", data);
        try {
          setLoading(true);
          const response = await fetchImage(replacedImgPath);
          console.log("🚀 ~ handleOnDoubleClickGridRow ~ response:", response);

          if (response && response.status === 200) {
            setLoading(false);
            const imageUrl = URL.createObjectURL(response.data);
            setImgURL(imageUrl);
            setOpenUriWindow(!openUriWindow);
            setImgPath(replacedImgPath);
          } else {
            setLoading(false);
            setImgURL(null);
          }

          setOpenUriWindow(true);
          setDetailedTestData(data);
        } catch (error) {
          console.log("🚀 ~ fetchUrinalysisImages ~ error:", error);
          setLoading(false);
          setDetailedTestData(null);
          setImgURL(null);
          showToast(
            ToastTypes.ERROR,
            "테스트 이미지 가져올 때 오류 생겼습니다. 디사 시도하십시오!"
          );
        }
      }
    }
  };

  const maskedUserNm = (name) => {
    // 표 상단 "마스킹 처리" 체크를 해제하면 원본을 그대로 보여준다.
    if (!maskingEnabled) {
      return name;
    }
    if (name && isString(name) && name.length <= 2) {
      return name;
    }
    if (isString(name) && !isEmpty(name)) {
      const firstChar = name.charAt(0);
      const lastChar = name.charAt(name.length - 1);
      const maskedSection = "*".repeat(name.length - 2);

      return `${firstChar}${maskedSection}${lastChar}`;
    }
  };

  /**
   * 연락처 표시 — 한국 번호 기준으로 국번 / 중간 / 뒤 4자리로 끊어 하이픈을 넣는다.
   * 국번은 02(서울)만 2자리고 010 등 나머지는 3자리다.
   * 저장 형식에 하이픈이 섞여 있어 숫자만 뽑아 다시 조립한다.
   *   마스킹 ON  → 01012345678 → 010-****-****, 021234567 → 02-***-****
   *   마스킹 OFF → 01012345678 → 010-1234-5678, 021234567 → 02-123-4567
   * 국번 판단이 불가능한 6자리 이하는 포맷 없이 둔다(켜져 있으면 전부 가림).
   */
  const maskedHpNo = (hpNo) => {
    if (!isString(hpNo) || isEmpty(hpNo)) {
      return hpNo;
    }

    const digits = hpNo.replace(/[^0-9]/g, "");
    if (digits.length < 7) {
      return maskingEnabled ? "*".repeat(digits.length) : hpNo;
    }

    const head = digits.startsWith("02") ? digits.slice(0, 2) : digits.slice(0, 3);
    const middle = digits.slice(head.length, -4);
    const tail = digits.slice(-4);
    // 7자리처럼 중간 자리가 없는 번호는 국번-뒤4자리 두 덩어리로만 끊는다.
    const rest = middle ? [middle, tail] : [tail];

    if (!maskingEnabled) {
      return [head, ...rest].join("-");
    }
    return [head, ...rest.map((part) => "*".repeat(part.length))].join("-");
  };

  /**
   * C값 제외 체크 박스 클릭 이벤트 핸들
   * @param {체크박스 클릭 이벤트} e
   */
  const handleOnChangeCheckboxCVal = (e) => {
    setCheckBoxCVal(e.target.checked);
    //console.log("🚀 ~ handleOnChangeCheckboxCVal ~ e.target.checked:", e.target.checked);
    if (e.target.checked) {
      setOrgList(list);
      const filteredList = list.filter(
        (item) => !item.c_val || Number(item.c_val) === 0
      );
      //console.log("🚀 ~ handleOnChangeCheckboxCVal ~ filteredList:", filteredList);
      setList(filteredList);
      createGraphData(filteredList);
    } else {
      setList(orgList);
      createGraphData(orgList);
    }
  };

  const handleOnChangeCheckboxBalancedRGB = (e) => {
    setRequestParameters((preVal) => {
      return {
        ...preVal,
        useBalancedRGB: e.target.checked ? "Y" : "N",
      };
    });
  };

  const handleOnChangeCheckboxRGB1 = (e) => {
    setRequestParameters((preVal) => {
      return {
        ...preVal,
        useRGB1: e.target.checked ? "Y" : "N",
      };
    });
  };

  const checkListData = (list) => {
    if (list && !isEmpty(list) && isArray(list)) {
      if (checkBoxCVal) {
        const filteredList = list.filter(
          (item) => !item.c_val || Number(item.c_val) === 0
        );
        //console.log("🚀 ~ handleOnChangeCheckboxCVal ~ filteredList:", filteredList);
        setList(filteredList);
        createGraphData(filteredList);
      } else {
        setList(list);
        createGraphData(list);
      }
    }
  };

  /**
   *
   * @param {*} list
   * 그래프 데이터 적용
   */
  function createGraphData(list) {
    if (list && !isEmpty(list) && isArray(list)) {
      const createdDataGrid = list.map((item, index) => {
        return {
          index: index + 1,
          test_seq_num: item.test_seq_num,
          K_상수: Number(item.val_constant_k),
          L_상수: Number(item.lab_distance),
          G_상수: Number(item.g_val),
          FP: Number(item.insp_value),
        };
      });
      // console.log("🚀 ~ gridDataTmp ~ gridDataTmp:", createdDataGrid);
      setGridData(createdDataGrid);
    }
  }

  const getDeviceModel = (deviceModel) => {
    if (deviceModel && isString(deviceModel) && !isEmpty(deviceModel)) {
      if (deviceModel.includes("iPhone")) {
        return "iPhone";
      } else {
        return "Android";
      }
    } else {
      return "";
    }
  };

  /**
   * 엑셀 다운로드 기능
   */
  const downloadExcel = () => {
    if (isEmpty(list) && isEmpty(listUrinalysis)) {
      showToast("warning", "안내", "다운받을 데이터가 없습니다.");
      return;
    }
    let ws;
    let testName = "";
    // 활성산소
    if (isOxygenStrip()) {
      testName = "활성산소";
      // 예전에는 ws["A1"].v ... 처럼 "열 위치"로 헤더를 덮어썼는데, 백엔드 응답 필드가
      // 바뀌면 라벨이 통째로 밀려 엉뚱한 열에 붙는다. (2026-08-03 변경으로 실제로 I열부터
      // 전부 어긋났다) 필드명 → 라벨을 명시적으로 매핑해 순서/개수 변화에 영향받지 않게 한다.
      const fixedData = list.map((row) => ({
        "테스트 일련번호": row.test_seq_num,
        사용자명: row.user_nm,
        검사일자: row.inst_dt,
        "촬영 환경": row.description,
        "가입된 디바이스": row.device_model,
        "촬영된 디바이스": row.info_model,
        밝기: row.brightness,
        "기준(FP)": row.insp_fp,
        "기준(단계)": row.insp_level,
        ...Object.fromEntries(
          OXYGEN_FP_COLUMNS.map((col) => [`검증 FP(${col.label})`, row[col.id]])
        ),
        "RGB(측정된)": row.insp_rgb,
        "RGB2(보정된)": row.insp_rgb2,
        "RGB3(흰색 패드)": row.insp_rgb3,
        LAB: row.lab,
        LAB1: row.lab1,
        LAB3: row.lab3,
        "Y(G)": row.yg,
        Cb: row.cb,
        Cr: row.cr,
        "Cb-Cr": row.distanceCbCr,
        RGB거리: row.distanceRGB,
        C: row.c,
        M: row.m,
        Y: row.y,
        "K-소수": row.kval,
        "K-상수": row.kconstant,
        HSL: row.h,
        델타: row.deltaPrime,
        "파일 위치": row.file_path,
      }));
      ws = utils.json_to_sheet(fixedData);
      ws["!cols"] = ws["!cols"] || [];
    }
    //모디엠 소변검사 스트립
    else if (isModimUrinalysisStrip() || isShinyanUrinalysisStrip()) {
      testName = "소변검사";
      const fixedData = listUrinalysis.map((row) => ({
        일련번호: row.test_seq_num,
        항목: row.spec_nm,
        "촬영 환경": row.description,
        검사일자: row.inst_dt,
        "가입된 디바이스": row.device_model,
        "촬영된 디바이스": row.info_device_model,
        계정명: row.user_nm,
        그룹명: row.group_user_nm,
        밝기: row.brightness,
        "측정된 RGB(AI)": row.rgb_original_ai,
        "측정된 Lab(AI)": row.lab_original_ai,
        "보정된 RGB(AI)": row.rgb_correction_ai,
        "보정된 Lab(AI)": row.lab_correction_ai,
        "화이트 RGB(AI)": row.rgb_white_ai,
        "모바일 단계": row.insp_level,
        "검증 단계": row.calculate_lvl,
        "예측 단계": row.predict_lvl,
        "측정된 RGB": row.insp_rgb,
        "보정된 RGB": row.gray_scaled_rgb,
        Lab: row.lab,
        "Y(G)": row.yg,
        Cb: row.cb,
        Cr: row.cr,
        "Cb-Cr": row.distanceCbCr,
        "RGB 거리": row.distanceRGB,
        C: row.c,
        M: row.m,
        Y: row.y,
        "K-소수": row.kval,
        "K-상수": row.kconstant,
        HSL: row.h,
        델타: row.deltaPrime,
        "R비율":row.ratioR,
        "G비율":row.ratioG,
        "B비율":row.ratioB,
        "G-B비율":row.ratioGminusB,
        "RGB비율합":row.ratioSum,
        "파일 위치": row.file_path,
        "openCV 실행": row.exec_opencv,
        "인공지능 실행": row.exec_ai,
      }));

      ws = utils.json_to_sheet(fixedData);
    }

    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Sheet1");
    const excelBuffer = write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // const fileName = "test_data.xlsx";
    const fileName =
      getCurrentDateTimeString() + "_" + testName + "_" + ".xlsx";

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleOnCloseImagePreview = () => {
    setOpenWindow(false);
  };

  const handleOnKeyDown = async (e) => {
    //console.log("🚀 ~ handleOnKeyDown ~ e:", e);
    if (e && e.keyCode === 13) {
      handleOnClickSearchButton();
    }
  };

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    payload,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        fontWeight={"bold"}
        fontSize={"24px"}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
        {/* {payload.name} */}
      </text>
    );
  };

  const getPercentValue = (item, type) => {
    //console.log("PERCENT ITEM => ", item);
    //console.log("PIE DATAS => ", pieDatas);
    let totalValue = 0;
    if (type === "base") {
      totalValue = baseLvlPieDatas.reduce((accumulator, currentValue) => {
        return accumulator + currentValue.value;
      }, 0);
    } else {
      totalValue = pieDatas.reduce((accumulator, currentValue) => {
        return accumulator + currentValue.value;
      }, 0);
    }

    //console.log("🚀 ~ totalValue ~ totalValue:", totalValue);
    const percent = Math.round((item.value / totalValue) * 100);
    return `${percent}% (${item.value})`;
    // return +"%" + "(" + item.value + ")";
  };

/**
 * 정렬 기능
 */
  useEffect(() => {
    handleOnClickSearchButton();
  }, [sortOrders]);

  const renderSortIcon = (col) => {
    const order = sortOrders[col];
    if (order === 'ASC') return <FaSortUp />;
    if (order === 'DESC') return <FaSortDown />;
    return <FaSort />;
  };

  const handleOnClickColumn = (col)  => {
    setSortOrders((prev) => {
      const current = prev[col];  // 현재 정렬 상태 확인

      if (current === undefined) {    // 초기값 없으면 바로 ASC로 추가
          return {
          ...prev,
          [col]: 'ASC',
          };
      }

      let nextOrder;
      if (current === 'ASC') nextOrder = 'DESC';
      else if (current === 'DESC') nextOrder = null;
      else nextOrder = 'ASC';

      const newSortOrders = { ...prev, [col]: nextOrder };
      if (nextOrder === null) {   // 정렬 없으면 삭제
          delete newSortOrders[col];
      }
      return newSortOrders;
    });
};
  useEffect(() => {
    if (!isEmpty(requestParameters.stripType)) {
      const splittedCustSeqStrip = requestParameters.stripType.split("_");
      // console.log("🚀 ~ isUriStrip ~ splittedCustSeqStrip:", splittedCustSeqStrip);
      if (splittedCustSeqStrip.length === 2) {
        if (multiStripItemsRef && multiStripItemsRef.current) {
          multiStripItemsRef.current.clearSelectedFields();
        }
        setList([]);
        setListUrinalysis([]);
        setRequestParameters((preVal) => {
          return {
            ...preVal,
            custSeq: splittedCustSeqStrip[0],
            stripId: splittedCustSeqStrip[1],
          };
        });
        fetchStripItems(splittedCustSeqStrip[0], splittedCustSeqStrip[1]);
      }
    }
  }, [requestParameters.stripType]);

  /**
   * 시뮬레이션 계산 결과(검증 단계 = calculate_lvl)와 AI 예측(predict_lvl)이
   * 현장 판정값(모바일 단계 = insp_level)과 얼마나 일치하는지 집계.
   * 파라미터를 바꿨을 때 결과가 좋아졌는지 바로 판단하기 위한 지표.
   * 기준값(모바일 단계)이 없는 행은 채점할 수 없으므로 모수에서 제외한다.
   */
  /**
   * 단계 값 파싱.
   * calculate_lvl 은 "계산값 (기준값)" 형식(예: "4 (3)")으로 내려오므로
   * 앞쪽 정수만 취한다. insp_level 처럼 "3" 형태도 그대로 처리된다.
   */
  const parseLevel = (raw) => {
    if (raw === null || raw === undefined) return null;
    const matched = String(raw).match(/-?\d+/);
    return matched ? Number(matched[0]) : null;
  };

  const calculateAccuracy = (rows) => {
    const summary = {
      total: 0,
      baseline: 0, // 기준값(모바일 단계)이 있는 건수
      calc: { match: 0, off1: 0, off2plus: 0, evaluated: 0 },
      ai: { match: 0, off1: 0, off2plus: 0, evaluated: 0 },
    };
    if (!isArray(rows) || isEmpty(rows)) return summary;

    summary.total = rows.length;
    rows.forEach((row) => {
      const answer = parseLevel(row.insp_level); // 모바일 단계 = 비교 기준
      if (answer === null) return;
      summary.baseline += 1;

      const score = (raw, bucket) => {
        const value = parseLevel(raw);
        if (value === null) return;
        bucket.evaluated += 1;
        const gap = Math.abs(value - answer);
        if (gap === 0) bucket.match += 1;
        else if (gap === 1) bucket.off1 += 1;
        else bucket.off2plus += 1;
      };

      score(row.calculate_lvl, summary.calc); // 검증 단계
      score(row.predict_lvl, summary.ai); // 예측 단계
    });
    return summary;
  };

  const formatRate = (matched, evaluated) =>
    evaluated > 0 ? `${((matched / evaluated) * 100).toFixed(1)}%` : "-";

  const createAccuracySummary = () => {
    const rows = !isEmpty(listUrinalysis) ? listUrinalysis : list;
    if (isEmpty(rows)) return null;

    const summary = calculateAccuracy(rows);
    const metrics = [
      { key: "calc", label: "검증 일치율", data: summary.calc, tone: "text-blue-600" },
      { key: "ai", label: "예측 일치율", data: summary.ai, tone: "text-indigo-600" },
    ].filter((m) => m.data.evaluated > 0);

    return (
      <div className="mt-4 shrink-0 rounded-xl bg-white px-5 py-3 shadow-sm ring-1 ring-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="shrink-0 text-sm font-semibold text-gray-900">조회 결과 요약</span>
            {summaryCollapsed && (
              <div className="motion-fade-in flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                <span className="tabular-nums text-slate-600">
                  총 <span className="font-semibold text-gray-900">{summary.total}</span>건
                </span>

                {metrics.map((metric) => (
                  <span key={metric.key} className="flex items-baseline gap-x-1 tabular-nums">
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{metric.label}</span>
                    <span className={`font-semibold ${metric.tone}`}>
                      {formatRate(metric.data.match, metric.data.evaluated)}
                    </span>
                    <span className="text-xs text-slate-400">
                      ({metric.data.match}/{metric.data.evaluated})
                    </span>
                  </span>
                ))}

                {metrics.length > 0 && (
                  <span className="flex items-center gap-x-1">
                    <span className="text-slate-400">·</span>
                    <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold tabular-nums text-green-700 ring-1 ring-inset ring-green-600/20">
                      정확 {summary.calc.match}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold tabular-nums text-amber-700 ring-1 ring-inset ring-amber-600/20">
                      ±1 {summary.calc.off1}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold tabular-nums text-red-700 ring-1 ring-inset ring-red-600/10">
                      ±2+ {summary.calc.off2plus}
                    </span>
                  </span>
                )}

                {metrics.length === 0 && (
                  <span className="text-slate-500">
                    <span className="text-slate-400">·</span> 기준값 없어 일치율 계산 불가
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            aria-expanded={!summaryCollapsed}
            title={summaryCollapsed ? "펼치기" : "접기"}
            className="motion-press -mr-1 inline-flex items-center justify-center rounded-md p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-700"
            onClick={() => setSummaryCollapsed((prev) => !prev)}
          >
            <span className="sr-only">{summaryCollapsed ? "펼치기" : "접기"}</span>
            <ChevronDownIcon
              className={`h-5 w-5 transition-transform duration-200 ${summaryCollapsed ? "" : "rotate-180"}`}
              aria-hidden="true"
            />
          </button>
        </div>

        <div
          className={`overflow-hidden transition-all duration-200 ease-out ${
            summaryCollapsed ? "max-h-0 opacity-0" : "mt-3 max-h-40 opacity-100"
          }`}
        >
        <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">총 건수</div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{summary.total}</div>
          </div>

          {metrics.map((metric) => (
            <div key={metric.key}>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{metric.label}</div>
              <div className="mt-1 flex items-baseline gap-x-2">
                <span className={`text-2xl font-bold tabular-nums ${metric.tone}`}>
                  {formatRate(metric.data.match, metric.data.evaluated)}
                </span>
                <span className="text-xs tabular-nums text-slate-500">
                  ({metric.data.match}/{metric.data.evaluated})
                </span>
              </div>
            </div>
          ))}

          {metrics.length > 0 && (
            <div className="min-w-[220px]">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                오차 분포 (검증)
              </div>
              <div className="mt-2 flex items-center gap-x-1.5 text-xs">
                <span className="inline-flex items-center gap-x-1 rounded-full bg-green-50 px-2 py-0.5 font-semibold text-green-700 ring-1 ring-inset ring-green-600/20">
                  정확 {summary.calc.match}
                </span>
                <span className="inline-flex items-center gap-x-1 rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700 ring-1 ring-inset ring-amber-600/20">
                  ±1단계 {summary.calc.off1}
                </span>
                <span className="inline-flex items-center gap-x-1 rounded-full bg-red-50 px-2 py-0.5 font-semibold text-red-700 ring-1 ring-inset ring-red-600/10">
                  ±2단계+ {summary.calc.off2plus}
                </span>
              </div>
            </div>
          )}

          {metrics.length === 0 && (
            <div className="text-sm text-slate-500">
              조회된 {summary.total}건 모두 모바일 단계(비교 기준)가 비어 있어 일치율을 계산할 수 없습니다.
            </div>
          )}
        </div>
        </div>
      </div>
    );
  };

  const createPieChart = () => {
    return (
      <div
        className={`transition-max-height transition-transform ease-in duration-300 overflow-hidden  ${
          expanded ? "max-h-full" : "max-h-0"
        }`}
      >
        <div
          className={`mt-3 grid grid-cols-1 gap-x-3 gap-y-10 sm:grid-cols-1 xl:gap-x-8 border border-gray-300 rounded-md items-center ${
            SHOW_VERIFIED_LEVEL ? "lg:grid-cols-2" : "lg:grid-cols-1"
          }`}
        >
          <div className="group relative mt-3">
            <div className="flex items-center justify-center">
              <label className="text-lg font-bold text-gray-900">
                기준 통계 데이터
              </label>
            </div>
            <div className="flex items-center">
              <PieChart width={400} height={400}>
                <Pie
                  data={baseLvlPieDatas}
                  cx={"50%"}
                  cy={"50%"}
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={150}
                  fill="#FFF"
                  dataKey={"value"}
                >
                  {baseLvlPieDatas.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
              <div>
                <table className="min-w-full border-separate border-spacing-0">
                  <thead className="text-nowrap">
                    <tr>
                      <th className="border border-gray-300 bg-white bg-opacity-75 py-3 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6 lg:pl-8">
                        단계
                      </th>
                      <th className="border border-gray-300 bg-white bg-opacity-75 py-3 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6 lg:pl-8">
                        퍼센트
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {baseLvlPieDatas.map((item, index) => (
                      <tr key={index}>
                        <td className="border border-gray-200 py-4 px-4 text-left text-sm">
                          {item.name}
                        </td>
                        <td className="border border-gray-200 py-4 px-4 text-left text-sm">
                          {getPercentValue(item, "base")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* 검증 단계 필드가 응답에서 빠져서 감춰둔 패널. SHOW_VERIFIED_LEVEL 참고 */}
          {SHOW_VERIFIED_LEVEL && (
          <div className="group relative mt-3">
            <div className="flex items-center justify-center">
              <label className="text-lg font-bold text-gray-900">
                계산된 통계 데이터
              </label>
            </div>
            <div className="flex items-center">
              <PieChart width={400} height={400}>
                <Pie
                  data={pieDatas}
                  cx={"50%"}
                  cy={"50%"}
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={150}
                  fill="#FFF"
                  dataKey={"value"}
                >
                  {pieDatas.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
              <div>
                <table className="min-w-full border-separate border-spacing-0">
                  <thead className="text-nowrap">
                    <tr>
                      <th className="border border-gray-300 bg-white bg-opacity-75 py-3 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6 lg:pl-8">
                        단계
                      </th>
                      <th className="border border-gray-300 bg-white bg-opacity-75 py-3 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6 lg:pl-8">
                        퍼센트
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pieDatas.map((item, index) => (
                      <tr key={index}>
                        <td className="border border-gray-200 py-4 px-4 text-left text-sm">
                          {item.name}
                        </td>
                        <td className="border border-gray-200 py-4 px-4 text-left text-sm">
                          {getPercentValue(item, "calcualte")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * 조회 결과가 서버 상한에서 잘렸을 때의 안내.
   * 잘린 걸 모르면 "이 기간엔 이게 전부"라고 오해하게 되므로 표 바로 위에 띄운다.
   * 컴포넌트가 아니라 함수 호출로 쓴다 — FilterField 주석 참고.
   */
  const renderTruncationNotice = () =>
    resultTruncated ? (
      <div className="-mx-4 mb-2 flex shrink-0 items-start gap-x-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-200 sm:-mx-6 lg:-mx-8">
        <ExclamationTriangleIcon className="mt-px h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          조회 결과가 많아 <strong>{SIMULATION_MAX_RESULTS.toLocaleString()}건까지만</strong> 표시했습니다.
          나머지는 표와 엑셀 다운로드에 포함되지 않으니, 검사일자 기간을 좁혀서 다시 조회하세요.
        </span>
      </div>
    ) : null;

  /**
   * 표 상단 바. 왼쪽은 표시 옵션 블록(마스킹 / AI 예측), 오른쪽은 정렬 필드 선택.
   * 셋 다 조회 조건이 아니라 표 표시 옵션이므로 필터 카드가 아닌
   * 각 데이터 그리드 바로 위에 붙인다.
   * 컴포넌트가 아니라 함수 호출(`{renderSortFieldBar()}`)로 쓴다 — FilterField 주석 참고.
   */
  const renderSortFieldBar = () => (
    <div className="-mx-4 flex shrink-0 items-center justify-between gap-x-4 pb-2 sm:-mx-6 lg:-mx-8">
      {/* 표시 옵션 묶음 — 조회 조건(필터 카드)과 헷갈리지 않도록 별도 블록으로 뺀다. */}
      <div className="flex items-center gap-x-3 rounded-md bg-slate-50 px-3 py-1.5 ring-1 ring-inset ring-slate-200">
        {/* 이름·연락처는 기본 마스킹. 실제 값 확인이 필요할 때만 해제한다. */}
        <label className="flex cursor-pointer select-none items-center gap-x-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={maskingEnabled}
            onChange={(e) => setMaskingEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
          />
          마스킹 처리
        </label>
        <span className="h-4 w-px shrink-0 bg-slate-200" aria-hidden="true" />
        {/* aiChecked 는 표시 옵션이자 조회 파라미터라, 켠 뒤 다시 조회해야 값이 채워진다. */}
        <label className="flex cursor-pointer select-none items-center gap-x-2 text-xs text-slate-600">
          <input
            type="checkbox"
            name="aipredict"
            checked={!!requestParameters.aiChecked}
            onChange={(e) => {
              setRequestParameters((preVal) => {
                return {
                  ...preVal,
                  aiChecked: e.target.checked,
                };
              });
            }}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
          />
          AI 예측 단계 표시
        </label>
      </div>
      <div title="정렬 필드" className="flex items-center gap-x-2">
        <BarsArrowDownIcon
          className="h-5 w-5 shrink-0 text-gray-500"
          aria-hidden="true"
        />
        <div className="w-48">
          <SelectMenu
            options={sortingFields}
            selectedId={requestParameters.orderByField}
            onSelect={(val) => {
              console.log("onSelect sortingField ~ val:", val);
              setRequestParameters((preVal) => {
                return {
                  ...preVal,
                  orderByField: val.id,
                };
              });
            }}
          />
        </div>
      </div>
    </div>
  );

  const createOxygenDataGrid = () => {
    return (
      <div className="flex min-h-0 flex-1 flex-col max-w-full mt-6">
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col px-4 sm:px-6 lg:px-8">
              {renderTruncationNotice()}
              {renderSortFieldBar()}
              <div className="flex min-h-0 flex-1 flex-col mt-1">
                <div className="thin-scrollbar -mx-4 min-h-0 flex-1 overflow-auto rounded-xl ring-1 ring-slate-200 bg-white sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-slate-200 ">
                      <thead className="sticky top-0 z-10 bg-gradient-to-b from-slate-50 to-slate-200 border-b border-slate-300">
                        <tr className="whitespace-nowrap divide-x divide-slate-200">
                          <th
                            scope="col"
                            className="w-3 py-3.5 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          ></th>
                          <th
                            scope="col"
                            className="w-3 py-3.5 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            NO
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            촬영 환경
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            검사일자
                          </th>
                          <th
                            scope="col"
                            className="min-w-24 px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            디바이스(모델)
                          </th>
                          <th
                            scope="col"
                            className="min-w-24 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            사용자명
                          </th>
                          <th
                            scope="col"
                            className="min-w-28 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            연락처
                          </th>
                          <th
                            scope="col"
                            className="min-w-16 px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            밝기
                          </th>
                          <th
                            scope="col"
                            className="min-w-20 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            모바일 FP
                          </th>
                          <th
                            scope="col"
                            className="min-w-20 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            모바일 단계
                          </th>
                          {OXYGEN_FP_COLUMNS.map((col) => (
                            <th
                              key={col.id}
                              scope="col"
                              className="min-w-20 px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600"
                            >
                              <span className="block text-[10px] font-medium normal-case tracking-normal text-slate-400">
                                검증 FP
                              </span>
                              {col.label}
                            </th>
                          ))}
                          {SHOW_VERIFIED_LEVEL && (
                            <th
                              scope="col"
                              className="min-w-20 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                            >
                              검증 단계
                            </th>
                          )}
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            RGB(측정된) + Lab1
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            RGB2(보정된) + Lab2
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            RGB3(횐색 패드) + Lab3
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            Y(G)
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            Cb
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            Cr
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            Cb-Cr
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            RGB거리
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            C
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            M
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            Y
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            K-소수
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            K-상수
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            HSL
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            델타
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {list &&
                          list.map((data, index) => (
                            <tr
                              className={`cursor-pointer divide-x divide-slate-100 ${
                                selectedSeqs.includes(data.test_seq_num)
                                  ? "bg-blue-50 hover:bg-blue-100"
                                  : "hover:bg-slate-50"
                              }`}
                              onClick={(e) =>
                                handleOnClickGridRow(e, data.test_seq_num)
                              }
                              onDoubleClick={(e) =>
                                handleOnDoubleClickGridRow(e, data)
                              }
                              //key={person.email}
                            >
                              <td className="whitespace-nowrap py-4 text-left pl-4 pr-3 text-xs font-medium text-gray-900">
                                <input
                                  aria-describedby="grid_row"
                                  checked={selectedSeqs.includes(data.test_seq_num)}
                                  type="checkbox"
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) =>
                                    toggleSelectRow(data.test_seq_num, e.target.checked)
                                  }
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                />
                              </td>
                              <td className="whitespace-nowrap py-4 text-left pl-4 pr-3 text-xs font-medium text-gray-900">
                                {index + 1}
                              </td>
                              <td className="w-40 min-w-40 whitespace-normal px-2 py-3 text-left text-xs text-gray-600">
                                {data.description}
                              </td>
                              <td className="w-28 whitespace-nowrap px-3 py-4 text-left text-xs text-gray-600">
                                {data.inst_dt}
                              </td>
                              <td className="w-20 whitespace-nowrap px-3 py-4 text-left text-xs text-gray-600">
                                {getDeviceModel(
                                  data.info_model && !isEmpty(data.info_model)
                                    ? data.info_model
                                    : data.device_model
                                )}
                              </td>
                              <td className="w-32 whitespace-nowrap px-3 py-4 text-left text-xs text-gray-600">
                                {maskedUserNm(data.user_nm)}
                              </td>
                              <td className="w-32 whitespace-nowrap px-3 py-4 text-left text-xs text-gray-600">
                                {maskedHpNo(data.user_hp_no)}
                              </td>
                              <td className="w-28 whitespace-nowrap px-2 py-3 text-left text-xs text-yellow-300">
                                {data.brightness}
                              </td>
                              <td className="w-32 whitespace-nowrap font-semibold px-2 py-3 text-center text-sm text-rose-500">
                                {data.insp_fp}
                              </td>
                              <td className="w-32 whitespace-nowrap font-semibold px-2 py-3 text-center text-sm text-rose-500">
                                {data.insp_level}
                              </td>
                              {OXYGEN_FP_COLUMNS.map((col) => (
                                <td
                                  key={col.id}
                                  className="w-24 whitespace-nowrap px-2 py-3 text-center text-sm font-semibold tabular-nums text-rose-500"
                                >
                                  {data[col.id] ?? "-"}
                                </td>
                              ))}
                              {SHOW_VERIFIED_LEVEL && (
                                <td className="w-32 whitespace-nowrap font-semibold px-2 py-3 text-center text-sm text-rose-500">
                                  {data[VERIFIED_LEVEL_FIELD]}
                                </td>
                              )}
                              <td className="whitespace-nowrap px-3 py-4 text-left text-xs text-gray-600">
                                <div className="flex justify-center items-center">
                                  <span className="w-16">{data.insp_rgb}</span>
                                  <div
                                    className="mx-2 rounded-sm ring-1 ring-inset ring-black/10"
                                    style={{
                                      height: 32,
                                      width: 32,
                                      backgroundColor: rgbToCssColor(
                                        data.insp_rgb
                                      ),
                                    }}
                                  />
                                  <span className="w-28 ">[{data.lab1}]</span>
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-left text-xs text-gray-600">
                                <div className="flex justify-center items-center">
                                  <span className="w-16">{data.insp_rgb2}</span>
                                  <div
                                    className="mx-2 rounded-sm ring-1 ring-inset ring-black/10"
                                    style={{
                                      height: 32,
                                      width: 32,
                                      backgroundColor: rgbToCssColor(
                                        data.insp_rgb2
                                      ),
                                    }}
                                  />
                                  <span className="w-28 ">[{data.lab}]</span>
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-left text-xs text-gray-600">
                                <div className="flex justify-center items-center">
                                  <span className="w-16">{data.insp_rgb3}</span>
                                  <div
                                    className="mx-2 rounded-sm ring-1 ring-inset ring-black/10"
                                    style={{
                                      height: 32,
                                      width: 32,
                                      backgroundColor: rgbToCssColor(
                                        data.insp_rgb3
                                      ),
                                    }}
                                  />
                                  <span className="w-28 ">[{data.lab3}]</span>
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-center text-xs text-gray-600">
                                {data.yg}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-center text-xs text-gray-600">
                                {data.cb}
                              </td>

                              <td className="whitespace-nowrap px-3 py-4 text-center text-xs text-gray-600">
                                {data.cr}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-center text-xs text-gray-600">
                                {data.distanceCbCr}
                              </td>
                              <td className="whitespace-nowrap px-3 text-center py-4 text-xs text-gray-600">
                                {data.distanceRGB}
                              </td>
                              <td className="whitespace-nowrap px-3 text-center py-4 text-xs text-gray-600">
                                {data.c}
                              </td>
                              <td className="whitespace-nowrap px-3 text-center py-4 text-xs text-gray-600">
                                {data.m}
                              </td>
                              <td className="whitespace-nowrap px-3 text-center py-4 text-xs text-gray-600">
                                {data.y}
                              </td>
                              <td className="whitespace-nowrap px-3 text-center py-4 text-xs text-gray-600">
                                {data.kval}
                              </td>
                              <td className="whitespace-nowrap px-3 text-center py-4 text-xs text-gray-600">
                                {data.kconstant}
                              </td>
                              <td className="whitespace-nowrap px-3 text-center py-4 text-xs text-gray-600">
                                {data.h}
                              </td>
                              <td className="whitespace-nowrap px-3 text-center py-4 text-xs text-gray-600">
                                {data.deltaPrime}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   *
   * @returns 소변검사 데이터 그리드
   */
  const createModimUrinalysisDataGrid = () => {
    return (
      <div className="flex min-h-0 flex-1 flex-col max-w-full mt-6">
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col px-4 sm:px-6 lg:px-8">
              {renderTruncationNotice()}
              {renderSortFieldBar()}
              <div className="flex min-h-0 flex-1 flex-col mt-1">
                <div className="thin-scrollbar -mx-4 min-h-0 flex-1 overflow-auto rounded-xl ring-1 ring-slate-200 bg-white sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-slate-200 ">
                      <thead className="sticky top-0 z-10 bg-gradient-to-b from-slate-50 to-slate-200 border-b border-slate-300">
                        <tr className="whitespace-nowrap divide-x divide-slate-200">
                          <th
                            scope="col"
                            className="w-3 py-3.5 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          ></th>
                          <th
                            scope="col"
                            className="w-2 py-3.5 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            NO
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('spec_nm')}>
                            <div className="flex items-center gap-1">
                              항목 {renderSortIcon('spec_nm')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            촬영 환경
                          </th>
                          {/* <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                            onClick={() => handleOnClickColumn('exec_opencv')}>
                            <div className="flex items-center gap-1">
                              OpenCV {renderSortIcon('exec_opencv')}
                            </div>
                          </th> */}
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                            onClick={() => handleOnClickColumn('exec_ai')}>
                            <div className="flex items-center gap-1">
                              AI {renderSortIcon('exec_ai')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('instDt')}>
                            <div className="flex items-center gap-1">
                              검사일자 {renderSortIcon('instDt')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="min-w-14 px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('model')}>
                            <div className="flex items-center gap-1">
                              디바이스(모델) {renderSortIcon('model')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="min-w-20 px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('user_nm')}>
                            <div className="flex items-center gap-1">
                              계정명 {renderSortIcon('user_nm')}
                            </div>
                          </th>
                          {/* 정렬 제외: sortOrders 키가 JPQL ORDER BY 에 그대로 들어가므로 검증된 컬럼만 건다. */}
                          <th
                            scope="col"
                            className="min-w-28 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            연락처
                          </th>
                          {/* 계정명(user_nm) = tb_user_info.user_nm(앱 계정 본인),
                              그룹명(group_user_nm) = tb_user_group.group_nm(검사받은 그룹원). 정렬은 위와 같은 이유로 제외. */}
                          <th
                            scope="col"
                            className="min-w-20 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            그룹명
                          </th>
                          <th
                            scope="col"
                            className="min-w-20 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('brightness')}>
                            <div className="flex items-center gap-1">
                              밝기 {renderSortIcon('brightness')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="min-w-20 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator insp_level')}>
                            <div className="flex items-center gap-1">
                              모바일 단계 {renderSortIcon('Comparator insp_level')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="min-w-20 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator calculate_lvl')}>
                            <div className="flex items-center gap-1">
                              검증 단계 {renderSortIcon('Comparator calculate_lvl')}
                            </div>
                          </th>
                          {requestParameters.aiChecked ?
                          <th
                            scope="col"
                            className="min-w-20 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator predict_lvl')}>
                            <div className="flex items-center gap-1">
                              예측 단계 {renderSortIcon('Comparator predict_lvl')}
                            </div>
                          </th>
                          : <></>
                          }
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            측정된 RGB
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            보정된 RGB
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            Lab
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator yG')}>
                            <div className="flex items-center gap-1">
                              Y(G) {renderSortIcon('Comparator yG')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator Cb')}>
                            <div className="flex items-center gap-1">
                              Cb {renderSortIcon('Comparator Cb')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator Cr')}>
                            <div className="flex items-center gap-1">
                              Cr {renderSortIcon('Comparator Cr')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator distanceCbCr')}>
                            <div className="flex items-center gap-1">
                              Cb-Cr {renderSortIcon('Comparator distanceCbCr')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                          >
                            RGB거리
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator C')}>
                            <div className="flex items-center gap-1">
                              C {renderSortIcon('Comparator C')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator M')}>
                            <div className="flex items-center gap-1">
                              M {renderSortIcon('Comparator M')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator Y')}>
                            <div className="flex items-center gap-1">
                              Y {renderSortIcon('Comparator Y')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator kVal')}>
                            <div className="flex items-center gap-1">
                              K-소수 {renderSortIcon('Comparator kVal')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator kConstant')}>
                            <div className="flex items-center gap-1">
                              K-상수 {renderSortIcon('Comparator kConstant')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator H')}>
                            <div className="flex items-center gap-1">
                              HSL {renderSortIcon('Comparator H')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator deltaPrime')}>
                            <div className="flex items-center gap-1">
                              델타 {renderSortIcon('Comparator deltaPrime')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator ratioR')}>
                            <div className="flex items-center gap-1">
                              R비율 {renderSortIcon('Comparator ratioR')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator ratioG')}>
                            <div className="flex items-center gap-1">
                              G비율 {renderSortIcon('Comparator ratioG')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator ratioB')}>
                            <div className="flex items-center gap-1">
                              B비율 {renderSortIcon('Comparator ratioB')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator ratioGminusB')}>
                            <div className="flex items-center gap-1">
                              G-B비율 {renderSortIcon('Comparator ratioGminusB')}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                            onClick={() => handleOnClickColumn('Comparator ratioSum')}>
                            <div className="flex items-center gap-1">
                              RGB비율합 {renderSortIcon('Comparator ratioSum')}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {listUrinalysis &&
                          listUrinalysis.map((data, index) => (
                            <tr
                              key={index}
                              className={`cursor-pointer divide-x divide-slate-100 ${
                                selectedSeqs.includes(data.test_seq_num)
                                  ? "bg-blue-50 hover:bg-blue-100"
                                  : "hover:bg-slate-50"
                              }`}
                              onClick={(e) =>
                                handleOnClickGridRow(e, data.test_seq_num)
                              }
                              onDoubleClick={(e) =>
                                handleOnDoubleClickGridRow(e, data)
                              }
                              //key={person.email}
                            >
                              <td className="whitespace-nowrap text-left pl-4 pr-2 py-3 text-xs font-medium text-gray-900">
                                <input
                                  aria-describedby="grid_row"
                                  checked={selectedSeqs.includes(data.test_seq_num)}
                                  type="checkbox"
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) =>
                                    toggleSelectRow(data.test_seq_num, e.target.checked)
                                  }
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                />
                              </td>
                              <td className="whitespace-nowrap px-2 py-3 text-left  text-xs font-medium text-gray-900">
                                {index + 1}
                              </td>
                              <td className="w-28 whitespace-nowrap px-2 py-3 text-left text-xs text-yellow-300">
                                {data.spec_nm}
                              </td>
                              <td className="w-28 whitespace-nowrap px-2 py-3 text-left text-xs text-yellow-300">
                                {data.description}
                              </td>
                              {/* <td className="w-28 whitespace-nowrap px-2 py-3 text-left text-xs text-gray-600">
                                {data.exec_opencv}
                              </td> */}
                              <td className="w-28 whitespace-nowrap px-2 py-3 text-left text-xs text-gray-600">
                                {data.exec_ai}
                              </td>
                              <td className="w-28 whitespace-nowrap px-2 py-3 text-left text-xs text-gray-600">
                                {data.inst_dt}
                              </td>
                              <td className="w-14 whitespace-nowrap px-2 py-3 text-left text-xs text-gray-600">
                                {data.info_device_model &&
                                  !isEmpty(data.info_device_model)
                                  ? data.info_device_model
                                  : data.device_model
                                }
                              </td>
                              <td className="w-32 whitespace-nowrap px-2 py-3 text-left text-xs text-gray-600">
                                {maskedUserNm(data.user_nm)}
                              </td>
                              <td className="w-32 whitespace-nowrap px-2 py-3 text-left text-xs text-gray-600">
                                {maskedHpNo(data.user_hp_no)}
                              </td>
                              <td className="w-32 whitespace-nowrap px-2 py-3 text-left text-xs text-gray-600">
                                {maskedUserNm(data.group_user_nm) || "-"}
                              </td>
                              <td className="w-28 whitespace-nowrap px-2 py-3 text-left text-xs text-yellow-300">
                                {data.brightness}
                              </td>
                              <td className="w-32 whitespace-nowrap font-semibold px-2 py-3 text-left text-sm text-rose-500">
                                {data.insp_level}
                              </td>
                              <td className="w-32 whitespace-nowrap font-semibold px-2 py-3 text-left text-sm text-rose-500">
                                {data.calculate_lvl}
                              </td>
                              {requestParameters.aiChecked ?
                              <td className="w-32 whitespace-nowrap font-semibold px-2 py-3 text-left text-sm text-rose-500">
                                {data.predict_lvl}
                              </td>:<></>
                              }
                              <td className="whitespace-nowrap px-2 py-3 text-start text-xs text-gray-600">
                                <div className="flex justify-center items-center">
                                  <span className="w-28">{data.insp_rgb}</span>
                                  <div
                                    className="ml-2 rounded-sm ring-1 ring-inset ring-black/10"
                                    style={{
                                      height: 32,
                                      width: 32,
                                      backgroundColor: rgbToCssColor(
                                        data.insp_rgb
                                      ),
                                    }}
                                  />
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-2 py-3 text-start text-xs text-gray-600">
                                <div className="flex justify-center items-center">
                                  <span className="w-20">
                                    {data.gray_scaled_rgb}
                                  </span>
                                  <div
                                    className="ml-2 rounded-sm ring-1 ring-inset ring-black/10"
                                    style={{
                                      height: 32,
                                      width: 32,
                                      backgroundColor: rgbToCssColor(
                                        data.gray_scaled_rgb
                                      ),
                                    }}
                                  />
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-2 py-3 text-center text-xs text-gray-600">
                                {data.lab +
                                  getColorResult(data.listColorResults, "0001")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0002"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.yg +
                                  getColorResult(data.listColorResults, "0002")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0003"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.cb +
                                  getColorResult(data.listColorResults, "0003")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0004"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.cr +
                                  getColorResult(data.listColorResults, "0004")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0005"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.distanceCbCr +
                                  getColorResult(data.listColorResults, "0005")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0006"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.distanceRGB +
                                  getColorResult(data.listColorResults, "0006")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0007"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.c +
                                  getColorResult(data.listColorResults, "0007")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0008"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.m +
                                  getColorResult(data.listColorResults, "0008")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0009"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.y +
                                  getColorResult(data.listColorResults, "0009")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0010"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.kval +
                                  getColorResult(data.listColorResults, "0010")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0011"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.kconstant +
                                  getColorResult(data.listColorResults, "0011")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0012"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.h +
                                  getColorResult(data.listColorResults, "0012")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0013"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.deltaPrime +
                                  getColorResult(data.listColorResults, "0013")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0014"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.ratioR +
                                  getColorResult(data.listColorResults, "0014")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0015"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.ratioG +
                                  getColorResult(data.listColorResults, "0015")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0016"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.ratioB +
                                  getColorResult(data.listColorResults, "0016")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0017"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.ratioGminusB +
                                  getColorResult(data.listColorResults, "0017")}
                              </td>
                              <td
                                className={classNames(
                                  isActiveColorModel(
                                    data.list_active_color_models,
                                    "0018"
                                  )
                                    ? "text-rose-500 font-bold"
                                    : "text-gray-600",
                                  " whitespace-nowrap px-2 py-3 text-center text-xs"
                                )}
                              >
                                {data.ratioSum +
                                  getColorResult(data.listColorResults, "0018")}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isActiveColorModel = (list, specCode) => {
    // console.log("🚀 ~ isActiveColorModel ~ list:", list);
    // console.log("🚀 ~ isActiveColorModel ~ specCode:", specCode);
    if (!list || (isEmpty(list) && !isArray(list))) return false;
    return list.some((item) => item.color_spec_code === specCode);
  };

  /**
   * 해당 컬러 값이 범위에서 몇 단계로 설정되어 있는 거 표시하는 함수
   * @param {*} listColorResults
   * @param {*} colorCode
   * @returns
   */
  const getColorResult = (listColorResults, colorCode) => {
    // console.log("🚀 ~ getColorResult ~ listColorResults:", listColorResults);
    // console.log("🚀 ~ getColorResult ~ colorCode:", colorCode);
    if (!isEmpty(listColorResults) && isArray(listColorResults)) {
      const foundColorItem = listColorResults.find(
        (item) => item.colorTypeSpecCode === colorCode
      );
      // console.log("🚀 ~ getColorResult ~ foundColorItem:", foundColorItem);
      return foundColorItem ? `(${foundColorItem.level})` : "";
    } else {
      return "";
    }
  };

  const createOxygenLineChart = () => {
    return (
      <div className="flex w-full max-w-full mt-6">
        <LineChart width={window.innerWidth - 350} height={500} data={gridData}>
          <CartesianGrid strokeDasharray={"2 2"} />
          {/* <XAxis dataKey={"index"} /> */}
          <XAxis
            dataKey={"index"}
            tick={{ stroke: "black", fontSize: 14, strokeWidth: 0.5 }}
            tickMargin={6}
          />
          <YAxis />
          <Tooltip />
          <Legend verticalAlign="top" height={30} iconType="line" />
          <Line
            type={"monotone"}
            dataKey={"FP"}
            stroke="#0030CF"
            // label={{ fill: "black", fontSize: 12 }}
            activeDot={{ stroke: "black", strokeWidth: 2, r: 5 }}
            dot={{ stroke: "#0030CF", strokeWidth: 2, r: 3 }}
          />
          {/* <Line
                        type={"monotone"}
                        dataKey={"K_상수"}
                        stroke="#0030CF"
                        // label={{ fill: "black", fontSize: 12 }}
                        activeDot={{ stroke: "black", strokeWidth: 2, r: 5 }}
                        dot={{ stroke: "#0030CF", strokeWidth: 2, r: 3 }}
                    /> */}
          <Line
            type={"monotone"}
            dataKey={"L_상수"}
            stroke="#D5BF86"
            // label={{ fill: "black", fontSize: 12 }}
            activeDot={{ stroke: "black", strokeWidth: 2, r: 5 }}
            dot={{ stroke: "#D5BF86", strokeWidth: 2, r: 3 }}
          />
          <Line
            type={"monotone"}
            dataKey={"G_상수"}
            stroke="#388E3C"
            // label={{ fill: "black", fontSize: 12 }}
            activeDot={{ stroke: "black", strokeWidth: 2, r: 5 }}
            dot={{ stroke: "#388E3C", strokeWidth: 2, r: 3 }}
          />
          <Line
            type={"monotone"}
            dataKey={"checked"}
            stroke="red"
            // label={{ fill: "black", fontSize: 12 }}
            // activeDot={{ stroke: "red", strokeWidth: 3, r: 5 }}
            dot={{ stroke: "red", strokeWidth: 3, r: 5 }}
          />
        </LineChart>
        {/* <LineChart height={500} width={800} data={data}>
                    <CartesianGrid strokeDasharray={"2 2"} />
                    <XAxis dataKey={"name"} padding={{ left: 30, right: 30 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend verticalAlign="top" height={30} />
                    <Line type={"monotone"} dataKey={"lab"} stroke="#8884d8" activeDot={{ r: 8 }} />
                </LineChart> */}
      </div>
    );
  };

  const createModimUrinalysisLineChart = () => {
    return <div className="flex w-full max-w-full"></div>;
  };

  const isOxygenStrip = () => {
    return (
      requestParameters.custSeq === "1" && requestParameters.stripId === "2"
    );
  };

  const isModimUrinalysisStrip = () => {
    return (
      requestParameters.custSeq === "2" && requestParameters.stripId === "1"
    );
  };

  const isShinyanUrinalysisStrip = () => {
    return (
      requestParameters.custSeq === "3" && requestParameters.stripId === "1"
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="w-full max-w-full shrink-0 rounded-xl bg-white px-5 py-4 shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="min-w-0 flex-1">
            <div className="grid grid-cols-1 items-center gap-x-6 gap-y-3 md:grid-cols-2 xl:grid-cols-3">
              <div>
                <FilterField label="스트립 타입">
                <SelectMenu
                  options={stripTypes}
                  selectedId={requestParameters.stripType}
                  onSelect={(val) => {
                    console.log("🚀 ~ Simulation ~ val:", val);
                    setRequestParameters((preVal) => {
                      return {
                        ...preVal,
                        stripType: val.id,
                      };
                    });
                  }}
                />
                </FilterField>
              </div>
              {stripItems && !isEmpty(stripItems) && (
                <div>
                  <FilterField label="스트립 항목">
                  <MultipleSelectDropDown
                    options={stripItems}
                    ref={multiStripItemsRef}
                    onChange={(val) => {
                      // console.log("MULTI VALS => ", val);
                      let selectedItems = [];
                      if (val && !isEmpty(val)) {
                        selectedItems = val.map((item) => {
                          return item.id;
                        });

                        setRequestParameters((preVal) => {
                          return {
                            ...preVal,
                            stripItems: selectedItems,
                            groupCode: val[0].groupCode,
                          };
                        });
                      }
                      // console.log("🚀 ~ setRequestParameters ~ selectedItems:", selectedItems);
                    }}
                  />
                  </FilterField>
                </div>
              )}

              <div>
                <FilterField label="촬영 환경">
                <input
                  type="text"
                  maxLength={20}
                  className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  onKeyDown={(e) => handleOnKeyDown(e)}
                  onChange={(e) => {
                    setRequestParameters((preVal) => {
                      return {
                        ...preVal,
                        description: e.target.value,
                      };
                    });
                  }}
                />
                </FilterField>
              </div>
              <div>
                <FilterField label="사용자명">
                <input
                  type="text"
                  maxLength={20}
                  className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  onKeyDown={(e) => handleOnKeyDown(e)}
                  onChange={(e) => {
                    setRequestParameters((preVal) => {
                      return {
                        ...preVal,
                        userNm: e.target.value,
                      };
                    });
                  }}
                />
                </FilterField>
              </div>
              <div>
                <FilterField label="연락처">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={13}
                  className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  onKeyDown={(e) => handleOnKeyDown(e)}
                  onChange={(e) => {
                    setRequestParameters((preVal) => {
                      return {
                        ...preVal,
                        userHpNo: e.target.value,
                      };
                    });
                  }}
                />
                </FilterField>
              </div>
              <div>
                <FilterField label="디바이스(모델)">
                <SelectMenu
                  options={defaultDeviceModels}
                  selectedId={requestParameters.deviceType}
                  onSelect={(val) => {
                    setRequestParameters((preVal) => {
                      return {
                        ...preVal,
                        deviceType: val.id,
                      };
                    });
                  }}
                />
                </FilterField>
              </div>
              <div className="md:col-span-2">
                <FilterField label="검사일자">
                <div className="flex w-full flex-wrap items-center justify-start gap-y-2">
                  <input
                    type="date"
                    className="block w-full min-w-0 flex-1 basis-36 rounded-md border-0 py-1.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600"
                    value={requestParameters.startDate}
                    onChange={(e) => {
                      setRequestParameters((preVal) => {
                        return {
                          ...preVal,
                          startDate: e.target.value,
                        };
                      });
                    }}
                  />
                  <span className="flex w-8 justify-center items-center">~</span>
                  <input
                    type="date"
                    value={requestParameters.endDate}
                    onChange={(e) => {
                      setRequestParameters((preVal) => {
                        return {
                          ...preVal,
                          endDate: e.target.value,
                        };
                      });
                    }}
                    className="block w-full min-w-0 flex-1 basis-36 rounded-md border-0 py-1.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600"
                  />
                </div>
                </FilterField>
              </div>
            </div>

          </div>
          <div className="flex shrink-0 items-end justify-end gap-2 lg:flex-col lg:justify-end lg:pl-4">
            <button
              type="button"
              disabled={selectedSeqs.length < 2}
              title={
                selectedSeqs.length < 2
                  ? "비교할 행을 2개 이상 선택하세요"
                  : `선택한 ${selectedSeqs.length}건 비교`
              }
              className="motion-press inline-flex w-28 items-center justify-center gap-x-1.5 whitespace-nowrap rounded-md bg-white px-3 py-2.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={() => setShowCompare(true)}
            >
              <Square2StackIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
              비교{selectedSeqs.length > 0 ? ` ${selectedSeqs.length}` : ""}
            </button>
            <button
              type="button"
              title="엑셀 다운로드"
              className="motion-press inline-flex w-28 items-center justify-center gap-x-1.5 whitespace-nowrap rounded-md bg-white px-3 py-2.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={() => downloadExcel()}
            >
              <FaFileExcel aria-hidden="true" className="h-4 w-4 text-emerald-600" />
              엑셀
            </button>
            <button
              type="button"
              disabled={isSearching}
              aria-busy={isSearching}
              className="motion-press inline-flex w-28 items-center justify-center gap-x-1.5 whitespace-nowrap rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 caret-transparent"
              onClick={() => handleOnClickSearchButton()}
            >
              {isSearching ? (
                <>
                  <ArrowPathIcon className="-ml-0.5 h-4 w-4 animate-spin" aria-hidden="true" />
                  <span className="motion-fade-in">조회 중</span>
                </>
              ) : (
                <>
                  <MagnifyingGlassIcon className="-ml-0.5 h-4 w-4" aria-hidden="true" />
                  <span>조회</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* <div className="flex justify-end">
          {isOxygenStrip() && (
            <button
              type="button"
              className="justify-center text-nowrap  text-gray-700 border border-blue-700 hover:border-blue-900 hover:bg-gray-50 font-semibold py-1 px-2 rounded-md focus:outline-none inline-flex items-center mr-4 gap-x-1.5  text-sm  shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700"
              onClick={() => setExpanded(!expanded)}
            >
              <HiOutlineChartSquareBar
                className="-ml-0.5 h-5 w-5"
                aria-hidden="true"
              />
              통계 데이터 {expanded ? "접기" : "펼치기"}
            </button>
          )}
          <button
            type="button"
            className="inline-flex items-center mr-4 gap-x-1.5 rounded-md  py-1 px-2  text-nowrap text-sm font-semibold text-gray-700 border border-indigo-700 hover:border-indigo-900 hover:bg-gray-50 shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 "
            onClick={downloadExcel}
          >
            <DocumentArrowDownIcon
              className="-ml-0.5 h-5 w-5 "
              aria-hidden="true"
            />
            엑셀 다운로드
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600  py-1 px-2  text-sm text-nowrap font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={() => handleOnClickSearchButton()}
          >
            <MagnifyingGlassIcon
              className="-ml-0.5 h-5 w-5"
              aria-hidden="true"
            />
            조회
          </button>
        </div> */}
      </div>
      <CompareImagesDialog
        isOpen={showCompare}
        onClose={() => setShowCompare(false)}
        rows={getSelectedRows()}
      />
      {createAccuracySummary()}
      {createPieChart()}
      {/* {isOxygenStrip() && createOxygenLineChart()} */}
      {(isModimUrinalysisStrip() || isShinyanUrinalysisStrip()) &&
        createModimUrinalysisLineChart()}

      <RenderInWindows
        open={openWindow}
        setOpen={setOpenWindow}
        imagePath={imgPath}
      >
        <ImagePreview
          imgPath={imgPath}
          imgSource={imgURL}
          data={detailedTestData}
          masked={maskingEnabled}
          onClose={handleOnCloseImagePreview}
        />
      </RenderInWindows>
      <RenderInWindows
        open={openUriWindow}
        setOpen={setOpenUriWindow}
        imagePath={imgPath}
      >
        <ImagePreviewUrinalysis
          imgPath={imgPath}
          zipFile={zipFile}
          images={images}
          imgSource={imgURL}
          data={detailedTestData}
          masked={maskingEnabled}
          onClose={() => setOpenUriWindow(false)}
        />
      </RenderInWindows>
      {/**활선산소 테스트 */}
      {isOxygenStrip() && createOxygenDataGrid()}
      {/**모디엠 소변검사 테스트 */}
      {(isModimUrinalysisStrip() || isShinyanUrinalysisStrip()) &&
        createModimUrinalysisDataGrid()}
    </div>
  );
}
